import { forwardRef } from 'react'
import { type VariantProps, cva } from 'class-variance-authority'
import type { IconType } from 'react-icons'

import { cn } from '@/utils/cn'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip'

const buttonVariants = cva(
  'group/iconButton inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1 disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-zinc-100 text-zinc-900 hover:bg-zinc-200',
        secondary:
          'bg-zinc-800 text-zinc-300 hover:text-zinc-100 hover:bg-zinc-700',
        ghost:
          'bg-transparent hover:bg-zinc-800 rounded-md text-zinc-400 hover:text-zinc-100',
        dark: 'text-white hover:bg-zinc-700/50',
      },
      size: {
        default: 'h-10 w-10',
        sm: 'h-6 w-6',
        lg: 'h-12 w-12',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
)

const iconSize = { default: 'h-5 w-5', sm: 'h-4 w-4', lg: 'h-5 w-5' } as const

interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  label?: string
  Icon: IconType
  tooltip?: boolean
  iconClassName?: string
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      label,
      Icon,
      size = 'default',
      variant,
      className,
      iconClassName,
      tooltip,
      ...props
    },
    ref,
  ) => {
    const btn = (
      <button
        ref={ref}
        type='button'
        aria-label={label}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      >
        <Icon
          className={cn(
            'text-inherit',
            iconSize[size ?? 'default'],
            iconClassName,
          )}
        />
        <span className='sr-only'>{label}</span>
      </button>
    )

    if (!tooltip) return btn

    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>{btn}</TooltipTrigger>
          <TooltipContent side='bottom'>
            <p>{label}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  },
)

IconButton.displayName = 'IconButton'

export default IconButton
